export const IMAGE_TOOL = {
  propose: 'propose_image_generation',
  frame: 'apply_slide_frame',
  look: 'look_at_references'
} as const

export const IMAGE_PROPOSAL_CARD = 'image-proposal'

export const SLIDE_FRAME_CARD = 'slide-frame'

export const IMAGE_REFERENCES_MAX = 10

// The image model copies a frame of the post nearly whole, so one is sent only as the previous take of a revision.
export const OWN_FRAME_KIND = 'frame of this post'

export const isOwnFrame = (asset: { readonly kind: string; readonly linked: boolean }) => asset.kind === 'image' && asset.linked

// IMAGE-CHAT-DESIGN § Roles; `previous` is the image a revision edits.
export const REFERENCE_ROLES = ['product', 'character', 'outfit', 'location', 'previous'] as const

export const ATTACHED_IMAGE = /^\[Attached image ([\w-]+) · ([^\]\n]{0,200})\]$/

export const attachedImageLine = (id: string, label: string) => `[Attached image ${id} · ${label}]`

export const attachedImagesIn = (texts: readonly string[]) => [
  ...new Set(texts.flatMap(text => text.split('\n').flatMap(line => ATTACHED_IMAGE.exec(line)?.[1] ?? [])))
]

export type ReferenceRole = (typeof REFERENCE_ROLES)[number]

export type RoledReference = { readonly id: string; readonly role: ReferenceRole }

export const isReferenceRole = (value: unknown): value is ReferenceRole => (REFERENCE_ROLES as readonly unknown[]).includes(value)

// A picture added on the card starts as its own kind when that is a role, else as inspiration only.
export const roleOfKind = (kind: string): ReferenceRole => (isReferenceRole(kind) ? kind : 'location')

const ROLE_LEGEND: Record<ReferenceRole, string> = {
  product: 'the product: keep it exactly as photographed, at its real size',
  character: 'the character: keep the face and hair exactly',
  outfit: 'the outfit: keep the clothes, shoes and accessories exactly',
  location: 'the location: inspiration for the setting only, not a copy',
  previous: 'the previous take of this picture: keep it exactly (composition, people, light, colours) and change only what the prompt asks'
}

// The image model reads which picture plays which role here, so a role changed on the card needs no new prompt.
export const withReferenceLegend = (prompt: string, roles: readonly ReferenceRole[]) =>
  roles.length
    ? `Reference images, in order: ${roles.map((role, index) => `image ${index + 1} is ${ROLE_LEGEND[role]}`).join('; ')}.\n\n${prompt}`
    : prompt
